import { z } from 'zod'
import { api, del, get, patch, post, put } from '../axios'
import { paginated } from '../zod/pagination'
import {
    assistantSettings,
    chatSettings,
    ChatSettings,
    ConnectRequest,
    crawlSettings,
    installationCheck,
    ScreeningQuestion,
    VoiceInput,
    site,
    siteAnswer,
    siteDocument,
    siteOverview,
    siteVacancy,
    tryOutAnswer,
    unansweredQuestion,
    webChat,
    whatsappSettings,
    siteLimit,
} from './site'

export type ChatFilters = {
    search?: string
    outcome?: string
    from?: string
    to?: string
    page?: number
}

export type VacancyFilters = {
    search?: string
    filter?: 'agent' | 'open' | 'all'
    page?: number
}

export type DocumentFilters = {
    search?: string
    status?: string
    kind?: string
    page?: number
}

export type AssistantInput = {
    assistantName: string | null
    welcomeMessage: string | null
    capabilities: string[]
}

export type WhatsappInput = {
    whatsappBusinessAccountId: string
    phoneNumberId: string
    startMessageTemplateId: string
}

export type TryOutInput = {
    content: string
    messages: {
        role: 'visitor' | 'assistant'
        content: string
        vacancyIds?: string[]
    }[]
    assistantName?: string | null
    capabilities?: string[]
    voice?: VoiceInput
}

function query(params: Record<string, string | number | undefined>): string {
    const search = new URLSearchParams()

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            search.set(key, String(value))
        }
    })

    const string = search.toString()

    return string ? `?${string}` : ''
}

export function fetchSites() {
    return get(site.array(), '/sites')
}

export function fetchSite(siteId: string) {
    return get(site, `/sites/${siteId}`)
}

export function fetchSiteOverview(siteId: string) {
    return get(siteOverview, `/sites/${siteId}/overview`)
}

export function fetchSiteVacancies(siteId: string, filters: VacancyFilters = {}) {
    return get(
        paginated(siteVacancy),
        `/sites/${siteId}/vacancies${query({
            per_page: 25,
            page: filters.page,
            search: filters.search,
            status: filters.filter === 'open' ? 'open' : undefined,
            with_agent: filters.filter === 'agent' ? 1 : undefined,
        })}`
    )
}

export function applyStartMessage(siteId: string) {
    return post(
        z.object({
            applied: z.number(),
            skipped: z.number(),
        }),
        `/sites/${siteId}/whatsapp/apply-start-message`
    )
}

export function fetchSiteChats(siteId: string, filters: ChatFilters = {}) {
    return get(
        paginated(webChat),
        `/sites/${siteId}/chats${query({
            search: filters.search,
            outcome: filters.outcome,
            from: filters.from,
            to: filters.to,
            page: filters.page,
            per_page: 50,
        })}`
    )
}

export function fetchSiteChat(siteId: string, chatId: string) {
    return get(webChat, `/sites/${siteId}/chats/${chatId}`)
}

export function fetchSiteDocuments(siteId: string, filters: DocumentFilters = {}, perPage = 15) {
    // 15 per page, so the added answers below the pages stay close.
    return get(
        paginated(siteDocument),
        `/sites/${siteId}/documents${query({
            per_page: perPage,
            ...filters,
        })}`
    )
}

export function fetchDocument(siteId: string, documentId: string) {
    return post(z.object({ queued: z.number() }), `/sites/${siteId}/documents/${documentId}/fetch`)
}

export function resyncSite(siteId: string) {
    return post(z.object({ queued: z.number() }), `/sites/${siteId}/resync`)
}

export function fetchAssistant(siteId: string) {
    return get(assistantSettings, `/sites/${siteId}/assistant`)
}

export function updateAssistant(siteId: string, input: AssistantInput) {
    return patch(assistantSettings, `/sites/${siteId}/assistant`, input)
}

export function tryOutAssistant(siteId: string, input: TryOutInput) {
    return post(tryOutAnswer, `/sites/${siteId}/assistant/try-out`, input)
}

export function fetchWhatsapp(siteId: string) {
    return get(whatsappSettings, `/sites/${siteId}/whatsapp`)
}

export function updateWhatsapp(siteId: string, input: WhatsappInput) {
    return put(whatsappSettings, `/sites/${siteId}/whatsapp`, input)
}

export type AnswerInput = {
    question: string
    answer: string
    askedQuestion?: string
}

export function fetchUnanswered(siteId: string, days: number, search?: string) {
    return get(
        z.object({
            data: unansweredQuestion.array(),
            meta: z.object({ total: z.number() }),
        }),
        `/sites/${siteId}/unanswered${query({
            days,
            search,
        })}`
    )
}

export function fetchAnswers(siteId: string) {
    return get(siteAnswer.array(), `/sites/${siteId}/answers`)
}

export function saveAnswer(siteId: string, input: AnswerInput, answerId?: string) {
    return answerId
        ? patch(siteAnswer, `/sites/${siteId}/answers/${answerId}`, input)
        : post(siteAnswer, `/sites/${siteId}/answers`, input)
}

export function deleteAnswer(siteId: string, answerId: string) {
    return del(z.object({ deleted: z.boolean() }), `/sites/${siteId}/answers/${answerId}`)
}

export function authorizeSite(input: ConnectRequest) {
    return post(z.object({ redirectUrl: z.string() }), '/sites/authorize', input)
}

export function updateSite(siteId: string, input: { candidatesInWordpress: boolean }) {
    return patch(site, `/sites/${siteId}`, input)
}

export function disconnectSite(siteId: string) {
    return del(z.object({ message: z.string() }), `/sites/${siteId}`)
}

export type CreateSiteInput = {
    url: string
    name?: string
    confirmExtraWebsite?: boolean
}

export function createSite(input: CreateSiteInput) {
    return post(site, '/sites', input)
}

export function fetchSiteLimit() {
    return get(siteLimit, '/sites/limit')
}

export function checkInstallation(siteId: string) {
    return post(installationCheck, `/sites/${siteId}/check-installation`)
}

export function fetchChatSettings(siteId: string) {
    return get(chatSettings, `/sites/${siteId}/chat`)
}

export function updateChatSettings(siteId: string, input: Partial<ChatSettings>) {
    return patch(chatSettings, `/sites/${siteId}/chat`, input)
}

export type CrawlInput = {
    sitemapUrl: string | null
    exclude: string[]
    vacancyPaths: string[]
    notVacancyPaths: string[]
}

export function fetchCrawlSettings(siteId: string) {
    return get(crawlSettings, `/sites/${siteId}/crawl`)
}

export function updateCrawlSettings(siteId: string, input: CrawlInput) {
    return put(crawlSettings, `/sites/${siteId}/crawl`, input)
}

export type SuggestQuestionsInput = {
    feedback?: string
    previousQuestions?: string[]
}

export function suggestQuestions(siteId: string, vacancyId: string, input: SuggestQuestionsInput) {
    return post(z.object({ questions: z.string().array() }), `/sites/${siteId}/vacancies/${vacancyId}/questions`, input)
}

export function createScreening(siteId: string, vacancyId: string, questions: ScreeningQuestion[]) {
    return post(
        siteVacancy.extend({ publishingIssues: z.string().array().default([]) }),
        `/sites/${siteId}/vacancies/${vacancyId}/campaign`,
        { questions }
    )
}

export const siteFormStyle = z.object({
    style: z.record(z.string(), z.unknown()),
    saved: z.boolean(),
    forms: z.number(),
    applied: z.number().optional(),
})

export function fetchFormStyle(siteId: string) {
    return get(siteFormStyle, `/sites/${siteId}/form-style`)
}

export function updateFormStyle(siteId: string, style: Record<string, unknown>, apply: boolean) {
    return put(siteFormStyle, `/sites/${siteId}/form-style`, {
        ...style,
        apply,
    })
}

export async function uploadSiteLogo(siteId: string, logo: File): Promise<string> {
    const data = new FormData()
    data.append('logo', logo)

    const response = await api.post(`/sites/${siteId}/form-style/logo`, data, {
        transformRequest: [],
        headers: { 'Content-Type': undefined },
    })

    return z.object({ logoUrl: z.string() }).parse(response.data).logoUrl
}

export function updateVoice(siteId: string, input: VoiceInput) {
    return patch(assistantSettings, `/sites/${siteId}/assistant/voice`, input)
}

export function analyzeVoice(siteId: string) {
    return post(z.object({ queued: z.boolean() }), `/sites/${siteId}/assistant/voice/analyze`)
}
